/** Cytoscape.js stylesheet definitions — edge styles, category colors, state classes */

/** Edge type -> color + line style mapping */
export const EDGE_STYLES: Record<string, { color: string; lineStyle: string }> = {
  'relates-to': { color: '#6b7280', lineStyle: 'solid' },
  'depends-on': { color: '#3b82f6', lineStyle: 'solid' },
  'extends': { color: '#22c55e', lineStyle: 'solid' },
  'references': { color: '#9ca3af', lineStyle: 'dashed' },
  'contradicts': { color: '#ef4444', lineStyle: 'dashed' },
  'implements': { color: '#a855f7', lineStyle: 'solid' },
}

/** Category -> node color mapping (exported for legend + toolbar chips, GH-128) */
export const CATEGORY_COLORS: Record<string, string> = {
  guide: '#3b82f6',
  reference: '#8b5cf6',
  tutorial: '#22c55e',
  api: '#f59e0b',
  concept: '#ec4899',
}
export const DEFAULT_NODE_COLOR = '#6b7280'

/** Dimmed opacity for unfocused elements in focus mode */
const DIM_OPACITY = 0.08

/** Hover highlight: lighter dim for mouseover (not as strong as click focus) */
const HOVER_DIM_OPACITY = 0.15

/** Cytoscape stylesheet array — node/edge base styles, state classes, typed colors */
export const CY_STYLE = [
  // Node base styling
  {
    selector: 'node',
    style: {
      label: 'data(label)',
      'font-size': '10px',
      'font-family': 'Inter, system-ui, sans-serif',
      color: '#e5e7eb',
      'text-outline-color': '#111827',
      'text-outline-width': 1.5,
      'text-valign': 'bottom',
      'text-margin-y': 6,
      'text-max-width': '100px',
      'text-wrap': 'ellipsis',
      width: 'mapData(degree, 0, 20, 20, 50)',
      height: 'mapData(degree, 0, 20, 20, 50)',
      'background-color': DEFAULT_NODE_COLOR,
      'border-width': 1.5,
      'border-color': '#374151',
      'overlay-opacity': 0,
      'transition-property': 'opacity, border-color, border-width',
      'transition-duration': '200ms',
    },
  },
  // Node active/selected state
  {
    selector: 'node:active, node:selected',
    style: { 'border-color': '#60a5fa', 'border-width': 2.5 },
  },
  // Extracted entity nodes
  {
    selector: 'node[kind="entity"]',
    style: {
      shape: 'round-rectangle',
      width: 24,
      height: 14,
      'font-size': '8px',
      'background-color': '#14b8a6',
      'border-color': '#0f766e',
    },
  },
  // Dimmed state for unfocused nodes
  {
    selector: 'node.dimmed',
    style: { opacity: DIM_OPACITY },
  },
  // Hover-dimmed state (lighter than click focus)
  {
    selector: 'node.hover-dimmed',
    style: { opacity: HOVER_DIM_OPACITY },
  },
  // Highlighted node (connected to hovered node)
  {
    selector: 'node.highlighted',
    style: { opacity: 1, 'border-color': '#60a5fa', 'border-width': 2 },
  },
  // LoD: hide labels when zoomed out (must be before search-match so search wins)
  {
    selector: 'node.lod-no-label',
    style: { label: '' },
  },
  // Highlighted search match (overrides dimmed/hover/lod states — last wins)
  {
    selector: 'node.search-match',
    style: { 'border-color': '#f59e0b', 'border-width': 3, opacity: 1, label: 'data(label)' },
  },
  // Edge base styling — low default opacity to reduce visual noise
  {
    selector: 'edge',
    style: {
      width: 1,
      'line-color': '#6b7280',
      'target-arrow-color': '#6b7280',
      'target-arrow-shape': 'triangle',
      'arrow-scale': 0.7,
      'curve-style': 'bezier',
      opacity: 0.4,
      'transition-property': 'opacity, width',
      'transition-duration': '200ms',
    },
  },
  // Implicit edges (similarity) — even subtler
  {
    selector: 'edge[?implicit]',
    style: {
      'line-style': 'dotted',
      'line-color': '#4b5563',
      'target-arrow-shape': 'none',
      'curve-style': 'unbundled-bezier',
      opacity: 0.25,
      width: 0.8,
    },
  },
  // Highlighted edges (connected to hovered/focused node)
  {
    selector: 'edge.highlighted',
    style: { opacity: 0.9, width: 1.8 },
  },
  // Dimmed edges (click focus)
  {
    selector: 'edge.dimmed',
    style: { opacity: DIM_OPACITY },
  },
  // Hover-dimmed edges (lighter than click focus)
  {
    selector: 'edge.hover-dimmed',
    style: { opacity: HOVER_DIM_OPACITY },
  },
  // Per-type edge colors
  ...Object.entries(EDGE_STYLES).map(([type, style]) => ({
    selector: `edge[type="${type}"]`,
    style: {
      'line-color': style.color,
      'target-arrow-color': style.color,
      'line-style': style.lineStyle as 'solid' | 'dashed',
    },
  })),
  // Per-category node colors
  ...Object.entries(CATEGORY_COLORS).map(([cat, color]) => ({
    selector: `node[category="${cat}"]`,
    style: { 'background-color': color },
  })),
]
